import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TentIcon from '@mui/icons-material/HolidayVillage';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ShieldIcon from '@mui/icons-material/Shield';

export default function StoreCamperOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [form, setForm] = useState({
    orderStatus: 'placed',
    paymentStatus: 'paid',
    notes: ''
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get('/api/v1/admin/store/orders?orderType=camper_store', { headers });
      if (res.data.success) setOrders(res.data.data);
    } catch (err) {
      setError('Failed to load camper store orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleOpenEdit = (order) => {
    setSelectedOrder(order);
    setForm({
      orderStatus: order.orderStatus || 'placed',
      paymentStatus: order.paymentStatus || 'pending',
      notes: order.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSaveStatus = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(`/api/v1/admin/store/orders/${selectedOrder.id}/status`, form, { headers });
      setDialogOpen(false);
      fetchOrders();
    } catch (err) {
      alert('Failed to update camper order');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TentIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Camper's Store Orders (Camp & Rentals)</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage orders reserved by registered participants for upcoming camps, including rental gear and deposits.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell>Order Ref ID</TableCell>
                <TableCell>Participant Details</TableCell>
                <TableCell>Trek & Campsite Handover</TableCell>
                <TableCell>Rental & Purchase Items</TableCell>
                <TableCell>Rent / Deposit Breakdown</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Order Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No camper store orders recorded yet.</TableCell>
                </TableRow>
              ) : (
                orders.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">{o.orderNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(o.createdAt).toLocaleDateString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{o.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">📞 {o.customerPhone}</Typography>
                      {o.registrationId && (
                        <Typography variant="caption" display="block" color="primary">
                          Reg ID: #{o.registrationId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TentIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="600">
                          {o.registration?.trek?.name || 'Basecamp Handover'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Collect at Camp on arrival
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {o.items?.map((it) => (
                        <Box key={it.id} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" fontWeight="bold" display="block">
                            • {it.productName} {it.variantName ? `(${it.variantName})` : ''} x {it.quantity}
                          </Typography>
                          <Chip
                            label={it.itemType === 'rental' ? `Rental (${it.rentalStatus})` : 'Purchase'}
                            size="small"
                            color={it.itemType === 'rental' ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">Rent/Items: ₹{o.subtotal}</Typography>
                      {o.depositTotal > 0 && (
                        <Typography variant="caption" color="success.main" fontWeight="bold" display="block">
                          + Deposit: ₹{o.depositTotal}
                        </Typography>
                      )}
                      <strong>Total: ₹{Number(o.grandTotal).toLocaleString('en-IN')}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={o.paymentStatus.toUpperCase()}
                        size="small"
                        color={o.paymentStatus === 'paid' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={o.orderStatus.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={o.orderStatus === 'delivered' || o.orderStatus === 'completed' ? 'success' : 'primary'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(o)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Camper Order: {selectedOrder?.orderNumber}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Order Status"
              fullWidth
              value={form.orderStatus}
              onChange={(e) => setForm({ ...form, orderStatus: e.target.value })}
            >
              <MenuItem value="placed">Placed / Booked</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="ready_for_pickup">Ready for Campsite Handover</MenuItem>
              <MenuItem value="delivered">Handed Over to Camper at Camp</MenuItem>
              <MenuItem value="completed">Completed (Gear Returned)</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>

            <TextField
              select
              label="Payment Status"
              fullWidth
              value={form.paymentStatus}
              onChange={(e) => setForm({ ...form, paymentStatus: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>

            <TextField
              label="Campsite Handover Notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Size L verified at Sankri basecamp"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveStatus}>Save Order</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
